/**
 * Shared CSV parsing utilities (used by the lead import and the CRM import).
 *
 * Handles BOM, delimiter detection (comma / semicolon), and quoted fields.
 * Reading falls back from UTF-8 to Windows-1252 so German Excel exports with
 * umlauts (ä ö ü ß) are read correctly.
 */

/**
 * Parse a CSV string into structured data.
 *
 * @param {string} csvString - Raw CSV content
 * @param {number} [previewLimit=0] - Max number of rows to return (0 = all)
 * @returns {{ headers: string[], rows: Object[], totalRows: number }}
 */
export function parseCSV(csvString, previewLimit = 0) {
	// Remove BOM if present
	const cleanedString = csvString.replace(/^\uFEFF/, '');

	// Detect delimiter (comma or semicolon)
	const delimiter = detectDelimiter(cleanedString);

	// Split into lines, respecting quoted fields that may contain newlines
	const lines = splitLines(cleanedString).filter((line) => line.trim());

	if (lines.length === 0) {
		return { headers: [], rows: [], totalRows: 0 };
	}

	// Parse header row
	const headers = parseLine(lines[0], delimiter);

	// Parse data rows
	const totalRows = lines.length - 1;
	const rowsToParse = previewLimit > 0 ? lines.slice(1, previewLimit + 1) : lines.slice(1);

	const rows = rowsToParse.map((line) => {
		const values = parseLine(line, delimiter);
		const row = {};

		headers.forEach((header, index) => {
			row[header] = values[index] || '';
		});

		return row;
	});

	return { headers, rows, totalRows };
}

/**
 * Split CSV content into lines, respecting quoted fields that contain newlines.
 * @param {string} text - Raw CSV content
 * @returns {string[]} Array of logical CSV lines
 */
function splitLines(text) {
	const lines = [];
	let current = '';
	let inQuotes = false;

	for (let i = 0; i < text.length; i++) {
		const char = text[i];

		if (char === '"') {
			inQuotes = !inQuotes;
			current += char;
		} else if (!inQuotes && (char === '\n' || (char === '\r' && text[i + 1] === '\n'))) {
			lines.push(current);
			current = '';
			if (char === '\r') i++; // skip \n after \r
		} else {
			current += char;
		}
	}

	if (current) lines.push(current);
	return lines;
}

/**
 * Detect the delimiter used in the CSV
 * @param {string} csvString - CSV content
 * @returns {string} Detected delimiter
 */
function detectDelimiter(csvString) {
	const firstLine = csvString.split(/\r?\n/)[0] || '';

	// Count occurrences of potential delimiters in first line
	const commaCount = (firstLine.match(/,/g) || []).length;
	const semicolonCount = (firstLine.match(/;/g) || []).length;

	// Use semicolon if it appears more often (common in German/European CSVs)
	return semicolonCount > commaCount ? ';' : ',';
}

/**
 * Parse a single CSV line, handling quoted fields
 * @param {string} line - CSV line
 * @param {string} delimiter - Field delimiter
 * @returns {string[]} Array of field values
 */
function parseLine(line, delimiter) {
	const result = [];
	let current = '';
	let inQuotes = false;

	for (let i = 0; i < line.length; i++) {
		const char = line[i];
		const nextChar = line[i + 1];

		if (inQuotes) {
			if (char === '"' && nextChar === '"') {
				// Escaped quote
				current += '"';
				i++; // Skip next quote
			} else if (char === '"') {
				// End of quoted field
				inQuotes = false;
			} else {
				current += char;
			}
		} else if (char === '"') {
			// Start of quoted field
			inQuotes = true;
		} else if (char === delimiter) {
			// End of field
			result.push(current.trim());
			current = '';
		} else {
			current += char;
		}
	}

	// Don't forget the last field
	result.push(current.trim());

	return result;
}

/**
 * Read a file as text, decoding as UTF-8 and falling back to Windows-1252 when
 * the UTF-8 decode produces replacement characters (typical for older German
 * Excel exports). Returns the decoded string.
 *
 * @param {File} file - File object
 * @returns {Promise<string>} File content
 */
export function readFileAsText(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = (event) => {
			const buffer = event.target.result;
			resolve(decodeBuffer(buffer));
		};

		reader.onerror = (error) => {
			reject(new Error('Failed to read file: ' + error.message));
		};

		// Read as ArrayBuffer so we can choose the decoder ourselves.
		reader.readAsArrayBuffer(file);
	});
}

/**
 * Decode a CSV ArrayBuffer as UTF-8, falling back to Windows-1252 if the UTF-8
 * decode contains the replacement character (U+FFFD).
 *
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function decodeBuffer(buffer) {
	const bytes = new Uint8Array(buffer);

	const utf8 = new TextDecoder('utf-8').decode(bytes);
	if (!utf8.includes('�')) return utf8;

	try {
		return new TextDecoder('windows-1252').decode(bytes);
	} catch {
		// windows-1252 not supported by this runtime — keep the UTF-8 result.
		return utf8;
	}
}
